import { useQuery } from '@tanstack/react-query';

import { getQueryClient } from '@/api/queryClient';
import { graphql, type ResultOf, type VariablesOf } from '@/graphql';
import gqlClient from '@/utils/gqlClient';

import {
  getCartIdCookie,
  invalidateCartData,
  saveCartIdCookie,
} from '../utils/cartUtils';

export const mutation = graphql(`
  mutation SavePromotion($cartId: String, $promotionCode: String!) {
    cartSavePromotion(cartId: $cartId, promotionCode: $promotionCode) {
      cartId
      resultCategory
      errorCode
      invalidReason
    }
  }
`);

export type SavePromotionResult = ResultOf<typeof mutation>;
export type SavePromotionVariables = VariablesOf<typeof mutation>;

export function withQueryKey(vars: SavePromotionVariables) {
  return {
    queryKey: ['savePromotion', vars.cartId, vars.promotionCode],
    queryFn: async () => {
      if (!vars.promotionCode) return null;

      try {
        const data = await gqlClient().request(mutation, vars);
        const saveResult = data.cartSavePromotion;

        // Save new cart ID in case it changed.
        if (
          saveResult.cartId &&
          (!saveResult.invalidReason || !saveResult.errorCode)
        ) {
          saveCartIdCookie(saveResult.cartId);
        }

        // Cart was updated, so clear cache for any fetches that use the cart.
        invalidateCartData();

        return data;
      } catch (err: any) {
        if (err?.response?.status === 404) {
          return {
            cartSavePromotion: {
              cartId: null,
              resultCategory: 'user_exception',
              errorCode: 'not_found',
              invalidReason: 'not_found',
            },
          };
        }

        return {
          cartSavePromotion: {
            cartId: null,
            resultCategory: 'unexpected_error',
            errorCode: 'unexpected_error',
            invalidReason: 'unexpected_error',
          },
        };
      }
    },
  };
}

export async function performSavePromotion(promotionCode: string) {
  const queryClient = getQueryClient();
  const cartId = getCartIdCookie();
  return queryClient.fetchQuery(withQueryKey({ cartId, promotionCode }));
}

export function useSavePromotion(promotionCode: string) {
  const cartId = getCartIdCookie();
  return useQuery({
    ...withQueryKey({ cartId, promotionCode }),
    enabled: !!promotionCode && !!cartId,
  });
}
